"use client";

import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Database,
  Download,
  FileCode,
  FolderOpen,
  Keyboard,
  MousePointer,
  Play,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  downloadGeneratedTest,
  generatePlaywrightTest,
  isFileSystemAccessSupported,
  logTraceCompletionToDataDog,
  saveTestToDirectory,
} from "../../playwright-generator";
import {
  type MachineTrace,
  type TraceEvent,
  useTraceInspector,
} from "../../trace-inspector";

/**
 * TraceTab - DevTool tab for viewing machine execution traces
 *
 * Focused on TEST GENERATION:
 * - DOM events are FIRST-CLASS citizens (clicks, inputs, changes)
 * - Machine events show state transitions
 * - Snapshots are hidden in live view (included in export only)
 */
export function TraceTab() {
  const { trace, events, clearTrace, downloadTrace } = useTraceInspector();
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [newestFirst, setNewestFirst] = useState(true);
  const [isContextPanelOpen, setIsContextPanelOpen] = useState(true);

  // File System Access API state for direct folder saving
  const [exportFolderHandle, setExportFolderHandle] =
    useState<FileSystemDirectoryHandle | null>(null);
  const [exportFolderName, setExportFolderName] = useState<string | null>(null);
  const [lastSavedFile, setLastSavedFile] = useState<string | null>(null);

  const toggleExpand = (id: number) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Filter to show:
  // 1. Machine events (@xstate.event) - user-triggered actions
  // 2. Snapshots that represent async transitions (state changed without a user event)
  const machineEvents = useMemo(() => {
    const result: TraceEvent[] = [];
    // Track states we've seen transitions TO from user events
    let lastUserEventToState: string | undefined;

    for (const event of events) {
      if (event.inspectionType === "@xstate.event") {
        result.push(event);
        // Track where user events transition to
        if (event.toState) {
          lastUserEventToState = event.toState;
        }
      } else if (event.inspectionType === "@xstate.snapshot") {
        // Skip snapshots without proper state info
        if (!event.toState || !event.fromState) {
          continue;
        }

        // This snapshot represents an async transition if:
        // - fromState matches where we left off from user actions
        // - toState is different (state actually changed)
        // This captures loading states completing (e.g., createUserAndConsumer -> nextState)
        if (
          event.fromState === lastUserEventToState &&
          event.toState !== event.fromState
        ) {
          result.push({
            ...event,
            machineEvent: {
              type: "done.invoke", // Indicates async completion
            },
          });
          // Update tracking - the async state completion becomes the new baseline
          lastUserEventToState = event.toState;
        }
      }
    }

    return result;
  }, [events]);
  const displayEvents = newestFirst
    ? [...machineEvents].reverse()
    : machineEvents;

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getMachineEventColor = (eventType: string) => {
    if (eventType === "SUBMIT" || eventType === "NEXT")
      return "text-green-700 bg-green-50";
    if (eventType === "BACK") return "text-orange-700 bg-orange-50";
    if (eventType === "SKIP") return "text-purple-700 bg-purple-50";
    if (eventType === "ERROR" || eventType.includes("error"))
      return "text-red-700 bg-red-50";
    if (eventType === "done.invoke" || eventType.startsWith("done."))
      return "text-blue-700 bg-blue-50"; // Async completion
    return "text-blue-700 bg-blue-50";
  };

  // Count DOM events for summary
  const totalDomEvents = machineEvents.reduce(
    (acc, e) => acc + (e.uiHint?.domEvents?.length || 0),
    0,
  );

  // Reconstruct current context from trace
  const currentContext = useMemo(() => {
    if (!trace) return null;
    return reconstructContext(trace);
  }, [trace]);

  // Track the last state we auto-downloaded for (to avoid duplicate downloads)
  const lastAutoDownloadState = useRef<string | null>(null);

  // Handler for picking an export folder using File System Access API
  const handlePickExportFolder = async () => {
    if (!isFileSystemAccessSupported()) {
      console.warn("[TraceTab] File System Access API not supported");
      return;
    }

    try {
      // Show folder picker dialog
      // Type assertion needed as File System Access API types are not in standard lib
      const showDirectoryPicker = (
        window as Window & {
          showDirectoryPicker?: (options?: {
            mode?: string;
            startIn?: string;
          }) => Promise<FileSystemDirectoryHandle>;
        }
      ).showDirectoryPicker;
      if (!showDirectoryPicker) return;

      const handle = await showDirectoryPicker({
        mode: "readwrite",
        startIn: "downloads",
      });
      setExportFolderHandle(handle);
      setExportFolderName(handle.name);
      setLastSavedFile(null);
      console.log(`[TraceTab] Export folder set to: ${handle.name}`);
    } catch (error) {
      // User cancelled the picker
      if ((error as Error).name !== "AbortError") {
        console.error("[TraceTab] Failed to pick folder:", error);
      }
    }
  };

  // Handler for exporting test (uses folder if set, otherwise downloads)
  const handleExportTest = async () => {
    if (!trace) return;

    const test = generatePlaywrightTest(trace);

    if (exportFolderHandle) {
      // Save directly to the selected folder
      const filename = await saveTestToDirectory(test, exportFolderHandle);
      if (filename) {
        setLastSavedFile(filename);
        console.log(`[TraceTab] Saved test to folder: ${filename}`);
      } else {
        // Fall back to download if save failed (e.g., permission revoked)
        console.warn(
          "[TraceTab] Save to folder failed, falling back to download",
        );
        downloadGeneratedTest(test);
      }
    } else {
      // No folder selected, use regular download
      downloadGeneratedTest(test);
    }
  };

  // Detect flow completion based on FINISH/RESTART events (triggers restartFlow action)
  // These events indicate the user completed the kiosk flow
  const COMPLETION_EVENTS = ["FINISH", "RESTART"];

  // Get the last event type from the trace
  const lastEventType = useMemo(() => {
    if (machineEvents.length === 0) return null;
    const lastEvent = machineEvents[machineEvents.length - 1];
    return lastEvent.machineEvent?.type || null;
  }, [machineEvents]);

  useEffect(() => {
    if (!trace || !lastEventType) return;

    // Check if the last event is a completion event
    const isCompletionEvent = COMPLETION_EVENTS.includes(lastEventType);
    if (!isCompletionEvent) return;

    // Avoid duplicate logging for the same event
    if (lastAutoDownloadState.current === lastEventType) return;
    lastAutoDownloadState.current = lastEventType;

    // Generate test and log to DataDog
    console.log(
      `[TraceTab] Flow completed with ${lastEventType} event - logging to DataDog`,
    );
    const test = generatePlaywrightTest(trace);
    logTraceCompletionToDataDog(test, lastEventType);
  }, [lastEventType, trace]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Test Trace</h3>
          {trace && (
            <p className="text-xs text-gray-500">
              {machineEvents.length} actions | {totalDomEvents} DOM events
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {/* Folder picker button - only show if API is supported */}
          {isFileSystemAccessSupported() && (
            <button
              type="button"
              onClick={handlePickExportFolder}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1 ${
                exportFolderHandle
                  ? "bg-green-100 text-green-700 hover:bg-green-200 border border-green-300"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300"
              }`}
              title={
                exportFolderHandle
                  ? `Saving to: ${exportFolderName}`
                  : "Set export folder for tests"
              }
            >
              {exportFolderHandle ? (
                <>
                  <Check className="h-3 w-3" />
                  {exportFolderName}
                </>
              ) : (
                <>
                  <FolderOpen className="h-3 w-3" />
                  Folder
                </>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleExportTest}
            disabled={!trace || machineEvents.length === 0}
            className="px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded transition-colors flex items-center gap-1"
            title={
              exportFolderHandle
                ? `Save Playwright test to ${exportFolderName}`
                : "Generate Playwright test from trace"
            }
          >
            <FileCode className="h-3 w-3" />
            Test
          </button>
          <button
            type="button"
            onClick={() => downloadTrace()}
            disabled={!trace}
            className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded transition-colors flex items-center gap-1"
            title="Download full trace JSON (includes snapshots)"
          >
            <Download className="h-3 w-3" />
            JSON
          </button>
          <button
            type="button"
            onClick={() => clearTrace()}
            disabled={!trace}
            className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded transition-colors flex items-center gap-1"
            title="Clear trace"
          >
            <Trash2 className="h-3 w-3" />
            Clear
          </button>
        </div>
      </div>

      {/* Sort toggle */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">
          Showing machine events with captured DOM interactions
        </span>
        <button
          type="button"
          onClick={() => setNewestFirst(!newestFirst)}
          className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
        >
          {newestFirst ? "Newest first" : "Oldest first"}
        </button>
      </div>

      {/* Two-column layout: Events | Context */}
      <div className="flex-1 flex gap-3 min-h-0">
        {/* Event List - Left Column */}
        <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg overflow-y-auto">
          {!trace ? (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              <div className="text-center">
                <Play className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>No trace active</p>
                <p className="text-xs text-gray-400 mt-1">
                  Interact with the kiosk to capture events
                </p>
              </div>
            </div>
          ) : displayEvents.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              No events yet - interact with the kiosk
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {displayEvents.map((event) => (
                <TraceEventRow
                  key={event.id}
                  event={event}
                  isExpanded={expandedEvents.has(event.id)}
                  onToggle={() => toggleExpand(event.id)}
                  formatTime={formatTime}
                  getMachineEventColor={getMachineEventColor}
                />
              ))}
            </div>
          )}
        </div>

        {/* Context Panel - Right Column (Collapsible) */}
        {isContextPanelOpen && (
          <div className="w-64 flex-shrink-0 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-xs font-semibold text-gray-700">
                  Context
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsContextPanelOpen(false)}
                className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                title="Close context panel"
              >
                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {currentContext ? (
                <ContextDisplay context={currentContext} />
              ) : (
                <div className="text-xs text-gray-400 text-center py-4">
                  No context yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* Context Panel Toggle - When Closed */}
        {!isContextPanelOpen && (
          <button
            type="button"
            onClick={() => setIsContextPanelOpen(true)}
            className="w-8 flex-shrink-0 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
            title="Open context panel"
          >
            <ChevronLeft className="h-4 w-4 text-gray-500" />
          </button>
        )}
      </div>

      {/* Quick stats */}
      {trace && machineEvents.length > 0 && (
        <div className="mt-2 flex gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <MousePointer className="h-3 w-3" />
            {
              machineEvents.filter((e) =>
                e.uiHint?.domEvents?.some((d) => d.type === "click"),
              ).length
            }{" "}
            clicks
          </span>
          <span className="flex items-center gap-1">
            <Keyboard className="h-3 w-3" />
            {
              machineEvents.filter((e) =>
                e.uiHint?.domEvents?.some((d) => d.type === "input"),
              ).length
            }{" "}
            inputs
          </span>
        </div>
      )}

      {/* Last saved file indicator */}
      {lastSavedFile && exportFolderName && (
        <div className="mt-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200 flex items-center gap-1">
          <Check className="h-3 w-3" />
          Saved: {exportFolderName}/{lastSavedFile}
        </div>
      )}
    </div>
  );
}

/**
 * Reconstruct current context by applying all deltas
 */
function reconstructContext(trace: MachineTrace): Record<string, unknown> {
  let context = { ...trace.initialContext };

  for (const event of trace.events) {
    if (event.contextDelta) {
      const delta = event.contextDelta;

      // Apply added
      for (const [key, value] of Object.entries(delta.added)) {
        context[key] = value;
      }

      // Apply changed
      for (const [key, change] of Object.entries(delta.changed)) {
        context[key] = change.to;
      }

      // Apply removed
      for (const key of delta.removed) {
        delete context[key];
      }
    }

    // If there's a full snapshot, use it
    if (event.contextSnapshot) {
      context = { ...event.contextSnapshot };
    }
  }

  return context;
}

/**
 * Display context in a readable format
 */
function ContextDisplay({ context }: { context: Record<string, unknown> }) {
  // Separate config keys from user data
  const configKeys = [
    "practiceId",
    "appointmentId",
    "orgId",
    "orgName",
    "qrMode",
    "pendingPricesMode",
    "stripeReaderId",
  ];

  // Keys to skip entirely (noise or time-sensitive)
  const skipKeys = ["intakeFormSections", "otpTime", "otpSentAt", "timestamp"];

  const entries = Object.entries(context).filter(([key, value]) => {
    // Skip internal/redacted values
    if (key === "_error") return false;
    if (skipKeys.includes(key)) return false;
    if (typeof value === "string" && value.startsWith("[REDACTED]"))
      return false;
    if (typeof value === "string" && value.startsWith("[BASE64_IMAGE"))
      return false;
    return true;
  });

  const userEntries = entries.filter(([key]) => !configKeys.includes(key));
  const configEntries = entries.filter(([key]) => configKeys.includes(key));

  return (
    <div className="space-y-3">
      {/* User Data */}
      {userEntries.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-green-700 mb-1">
            User Data
          </div>
          <div className="space-y-1">
            {userEntries.map(([key, value]) => (
              <ContextEntry key={key} keyName={key} value={value} />
            ))}
          </div>
        </div>
      )}

      {/* Config */}
      {configEntries.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 mb-1">Config</div>
          <div className="space-y-1">
            {configEntries.map(([key, value]) => (
              <ContextEntry key={key} keyName={key} value={value} isConfig />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContextEntry({
  keyName,
  value,
  isConfig = false,
}: {
  keyName: string;
  value: unknown;
  isConfig?: boolean;
}) {
  const displayValue = formatContextValue(value);
  const isLongValue = displayValue.length > 30;

  return (
    <div className={`text-xs ${isConfig ? "text-gray-400" : "text-gray-600"}`}>
      <span className="font-mono text-gray-500">{keyName}:</span>{" "}
      {isLongValue ? (
        <details className="inline">
          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
            {typeof value === "object"
              ? Array.isArray(value)
                ? `[${(value as unknown[]).length}]`
                : "{...}"
              : `${displayValue.slice(0, 20)}...`}
          </summary>
          <pre className="mt-1 p-1 bg-gray-100 rounded text-xs overflow-x-auto whitespace-pre-wrap">
            {displayValue}
          </pre>
        </details>
      ) : (
        <span className={isConfig ? "text-gray-400" : "text-gray-700"}>
          {displayValue}
        </span>
      )}
    </div>
  );
}

function formatContextValue(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return `"${value}"`;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) return JSON.stringify(value, null, 2);
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

/**
 * Display a single DOM event in a readable format
 */
function DOMEventDisplay({
  domEvent,
}: {
  domEvent: { type: string; selector: string; value?: string };
}) {
  // Parse the selector to extract meaningful parts
  const { icon, label, action, value } = parseDOMEvent(domEvent);

  return (
    <div className="flex items-center gap-2 py-0.5">
      {/* Action icon */}
      <span className="text-gray-400">{icon}</span>

      {/* Action type */}
      <span
        className={`font-medium ${
          domEvent.type === "click"
            ? "text-blue-600"
            : domEvent.type === "input"
              ? "text-green-600"
              : "text-purple-600"
        }`}
      >
        {action}
      </span>

      {/* Target label */}
      <span className="text-gray-700">{label}</span>

      {/* Value (for input/change) */}
      {value && (
        <span className="text-gray-500">
          → <span className="font-mono text-gray-700">"{value}"</span>
        </span>
      )}
    </div>
  );
}

/**
 * Parse a DOM event into human-readable parts
 */
function parseDOMEvent(domEvent: {
  type: string;
  selector: string;
  value?: string;
}): {
  icon: string;
  label: string;
  action: string;
  value?: string;
} {
  const selector = domEvent.selector;

  // Extract label from various selector formats
  let label = selector;

  // getByRole("button", { name: "Next" }) -> "Next" button
  const roleMatch = selector.match(
    /getByRole\("([^"]+)",\s*\{\s*name:\s*"([^"]+)"\s*\}\)/,
  );
  if (roleMatch) {
    label = `"${roleMatch[2]}" ${roleMatch[1]}`;
  }

  // getByLabel("Street address") -> "Street address" input
  const labelMatch = selector.match(/getByLabel\("([^"]+)"\)/);
  if (labelMatch) {
    label = `"${labelMatch[1]}" input`;
  }

  // getByPlaceholder("...") -> placeholder input
  const placeholderMatch = selector.match(/getByPlaceholder\("([^"]+)"\)/);
  if (placeholderMatch) {
    const placeholder =
      placeholderMatch[1].length > 20
        ? `${placeholderMatch[1].slice(0, 20)}...`
        : placeholderMatch[1];
    label = `"${placeholder}" input`;
  }

  // locator(".pac-item") -> address suggestion
  if (selector.includes(".pac-item")) {
    label = "address suggestion";
  }

  // locator("[data-pharmacy-result]") -> pharmacy result
  if (selector.includes("[data-pharmacy-result]")) {
    label = "pharmacy result";
  }

  // locator("[data-signature-canvas]") -> signature canvas
  if (selector.includes("[data-signature-canvas]")) {
    label = "signature canvas";
  }

  // getByTestId
  const testIdMatch = selector.match(/getByTestId\("([^"]+)"\)/);
  if (testIdMatch) {
    label = `[${testIdMatch[1]}]`;
  }

  // getByRole("option", { name: "..." }) -> select option
  const optionMatch = selector.match(
    /getByRole\("option",\s*\{\s*name:\s*"([^"]+)"\s*\}\)/,
  );
  if (optionMatch) {
    label = `"${optionMatch[1]}" option`;
  }

  // getByRole("combobox", ...) -> dropdown
  const comboboxMatch = selector.match(
    /getByRole\("combobox"(?:,\s*\{\s*name:\s*"([^"]+)"\s*\})?\)/,
  );
  if (comboboxMatch) {
    label = comboboxMatch[1] ? `"${comboboxMatch[1]}" dropdown` : "dropdown";
  }

  // locator("#id") for combobox -> dropdown with ID
  const idMatch = selector.match(/locator\("#([^"]+)"\)/);
  if (idMatch) {
    label = `#${idMatch[1]} dropdown`;
  }

  const actionMap: Record<string, string> = {
    click: "Click",
    input: "Type",
    change: "Select",
    select: "Select",
  };

  const iconMap: Record<string, string> = {
    click: "👆",
    input: "⌨️",
    change: "📋",
    select: "📋",
  };

  // Special case for signature canvas
  if (selector.includes("[data-signature-canvas]")) {
    return {
      icon: "✍️",
      action: "Sign",
      label: "signature canvas",
      value: undefined,
    };
  }

  return {
    icon: iconMap[domEvent.type] || "•",
    action: actionMap[domEvent.type] || domEvent.type,
    label,
    value: domEvent.type !== "click" ? domEvent.value : undefined,
  };
}

interface TraceEventRowProps {
  event: TraceEvent;
  isExpanded: boolean;
  onToggle: () => void;
  formatTime: (ms: number) => string;
  getMachineEventColor: (type: string) => string;
}

function TraceEventRow({
  event,
  isExpanded,
  onToggle,
  formatTime,
  getMachineEventColor,
}: TraceEventRowProps) {
  const domEvents = event.uiHint?.domEvents || [];
  const hasDomEvents = domEvents.length > 0;

  return (
    <div className="hover:bg-gray-100 transition-colors">
      {/* Main Row */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center gap-3 text-left"
      >
        {/* Expand Icon */}
        <span className="text-gray-400 w-4">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>

        {/* Time */}
        <span className="text-xs font-mono text-gray-400 w-14">
          +{formatTime(event.relativeTime)}
        </span>

        {/* Machine Event */}
        {event.machineEvent && (
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded ${getMachineEventColor(
              event.machineEvent.type,
            )}`}
          >
            {event.machineEvent.type}
          </span>
        )}

        {/* State transition */}
        {event.fromState &&
          event.toState &&
          event.fromState !== event.toState && (
            <span className="text-xs text-gray-500">
              {event.fromState} → {event.toState}
            </span>
          )}

        {/* DOM events count badge */}
        {hasDomEvents && (
          <span className="ml-auto text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
            {domEvents.length} DOM
          </span>
        )}

        {/* Badge for async/loading states (no DOM events but state changes) */}
        {!hasDomEvents &&
          event.machineEvent &&
          event.toState &&
          event.fromState !== event.toState && (
            <span
              className={`ml-auto text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${
                event.machineEvent.type === "done.invoke" ||
                event.machineEvent.type.startsWith("done.")
                  ? "bg-green-100 text-green-700"
                  : "bg-blue-100 text-blue-700"
              }`}
            >
              {event.machineEvent.type === "done.invoke" ||
              event.machineEvent.type.startsWith("done.") ? (
                <span>✓</span>
              ) : (
                <span className="inline-block w-2 h-2 border border-blue-500 border-t-transparent rounded-full animate-spin" />
              )}
              {event.machineEvent.type === "done.invoke" ||
              event.machineEvent.type.startsWith("done.")
                ? "Done"
                : "Async"}
            </span>
          )}

        {/* Warning if no DOM events and no state change */}
        {!hasDomEvents &&
          event.machineEvent &&
          (!event.toState || event.fromState === event.toState) && (
            <span className="ml-auto text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
              No DOM
            </span>
          )}
      </button>

      {/* Expanded Details - DOM Events First */}
      {isExpanded && (
        <div className="px-3 pb-3 pl-10 space-y-2">
          {/* DOM Events - PRIMARY */}
          {hasDomEvents ? (
            <div>
              <span className="text-xs font-semibold text-green-700">
                DOM Actions:
              </span>
              <div className="mt-1 bg-green-50 p-2 rounded border border-green-200 space-y-1.5 text-xs">
                {domEvents.map((domEvent) => (
                  <DOMEventDisplay
                    key={`${domEvent.type}-${domEvent.selector}-${domEvent.timestamp}`}
                    domEvent={domEvent}
                  />
                ))}
              </div>
            </div>
          ) : event.toState && event.fromState !== event.toState ? (
            <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <span className="font-medium">Async State Transition</span>
              </div>
              <p className="mt-1 text-blue-500">
                This is a loading/async state (e.g., API call). The generated
                test will wait for the machine to transition out of{" "}
                <code className="bg-blue-100 px-1 rounded">
                  {event.fromState}
                </code>
                .
              </p>
            </div>
          ) : (
            <div className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded border border-yellow-200">
              No DOM events captured for this action. Check if the button/input
              has proper selectors.
            </div>
          )}

          {/* Event Data (collapsed by default, less important) */}
          {event.machineEvent?.data !== undefined && (
            <details className="text-xs">
              <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
                Event payload
              </summary>
              <pre className="mt-1 font-mono bg-gray-100 p-2 rounded border border-gray-200 overflow-x-auto text-gray-600">
                {JSON.stringify(event.machineEvent.data, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
